import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { db } from '../lib/firebase';
import LiveStreamModal from '../components/chat/LiveStreamModal';
import { doc, getDoc } from 'firebase/firestore';

import useChatStore from '../store/useChatStore';
import useAuthStore from '../store/useAuthStore';
import RichMessageRenderer from '../components/chat/RichMessageRenderer';
import MentionBar from '../components/chat/MentionBar';
import useConfigStore from '../store/useConfigStore';
import ModelSelector from '../components/chat/ModelSelector';
import PromptInput from '../components/chat/PromptInput';
import DesktopStatus from '../components/layout/DesktopStatus';
import { useDesktopStatus } from '../hooks/useDesktopStatus';
import { DesktopService } from '../services/desktopService';

// --- Icons ---
const BackIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>;
const BotIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 3v1.5M4.5 8.25H3m18 0h-1.5M4.5 12H3m18 0h-1.5m-15 3.75H3m18 0h-1.5M8.25 19.5V21M12 3v1.5m0 15V21m3.75-18v1.5m0 15V21m-9-1.5h10.5a2.25 2.25 0 002.25-2.25V6.75a2.25 2.25 0 00-2.25-2.25H6.75A2.25 2.25 0 004.5 6.75v10.5a2.25 2.25 0 002.25 2.25zm.75-12h9v9h-9v-9z" /></svg>;
const BrainIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.82 1.508-2.316a7.5 7.5 0 10-7.536 0c.85.496 1.508 1.333 1.508 2.316V18" /></svg>;
const TerminalIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}><path strokeLinecap="round" strokeLinejoin="round" d="M6.75 7.5l3 2.25-3 2.25m4.5 0h3m-9 8.25h13.5A2.25 2.25 0 0021 18V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v12a2.25 2.25 0 002.25 2.25z" /></svg>;
const FolderIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 9.776c.112-.017.227-.026.344-.026h15.812c.117 0 .232.009.344.026m-16.5 0a2.25 2.25 0 00-1.883 2.542l.857 6a2.25 2.25 0 002.227 1.932H19.05a2.25 2.25 0 002.227-1.932l.857-6a2.25 2.25 0 00-1.883-2.542m-16.5 0V6A2.25 2.25 0 016 3.75h3.879a1.5 1.5 0 011.06.44l2.122 2.12a1.5 1.5 0 001.06.44H18A2.25 2.25 0 0120.25 9v.776" /></svg>;
const CodeIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}><path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" /></svg>;
const EditIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" /></svg>;
const VideoIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 9.75v9a2.25 2.25 0 002.25 2.25z" /></svg>;
const PowerIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}><path strokeLinecap="round" strokeLinejoin="round" d="M5.636 5.636a9 9 0 1012.728 0M12 3v9" /></svg>;

// A lightweight custom toggle switch
const ToggleSwitch = ({ checked, onChange, disabled }) => (
    <button
        type="button"
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`${checked ? 'bg-indigo-500' : 'bg-slate-700'} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none`}
    >
        <span className={`${checked ? 'translate-x-4' : 'translate-x-0'} pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`} />
    </button>
);

const ChatRoomPage = () => {
    const { chatId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuthStore();
    const { activeChat, setActiveChat, messages, loading, subscribeToMessages, sendMessage, markChatAsRead, updateChatSettings, isAiThinking } = useChatStore();
    const { isOnline, resolvedDeviceId } = useDesktopStatus();
    const { aiConfigs } = useConfigStore();

    // Local state
    const [inputText, setInputText] = useState('');
    const [members, setMembers] = useState([]);
    const [isGhostMode, setIsGhostMode] = useState(false);

    // Dialog State
    const [terminalOpen, setTerminalOpen] = useState(false);
    const [cmdInput, setCmdInput] = useState('');

    const [filesOpen, setFilesOpen] = useState(false);
    const [fileInstruction, setFileInstruction] = useState('');

    const [isLiveOpen, setIsLiveOpen] = useState(false);
    const [shutdownOpen, setShutdownOpen] = useState(false);

    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [titleInput, setTitleInput] = useState('');

    const handleModelSelect = async (selection) => {
        if (!activeChat) return;
        try {
            const selectedConfig = aiConfigs?.configs?.[selection];
            const isOllama = selectedConfig?.provider === 'ollama';

            const settings = {
                modelConfigId: selection,
                aiConfigId: selection, // For cloud function compatibility
                localBrainEnabled: false,
                localModel: null
            };

            // Auto-route to online device if Ollama is selected and no device targeted
            if (isOllama && isOnline && !activeChat.targetDeviceId) {
                settings.targetDeviceId = resolvedDeviceId;
            }

            await updateChatSettings(activeChat.id, settings);
        } catch (err) {
            console.error("Failed to update model selection:", err);
        }
    };

    useEffect(() => {
        if (activeChat?.title) setTitleInput(activeChat.title);
    }, [activeChat]);

    const handleSaveTitle = async () => {
        setIsEditingTitle(false);
        if (titleInput.trim() && activeChat) {
            await updateChatSettings(activeChat.id, { title: titleInput });
        }
    };

    const handleExpertSelect = async (expertId) => {
        if (!activeChat) return;
        try {
            await updateChatSettings(activeChat.id, {
                expertId: expertId // Save to session
            });
        } catch (err) {
            console.error("Failed to update expert selection:", err);
        }
    };

    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const initialPromptHandled = useRef(false);

    // 1. Subscribe to Messages & Mark Read
    useEffect(() => {
        setActiveChat(chatId);
        const unsubscribe = subscribeToMessages(chatId);
        markChatAsRead(chatId);
        return () => unsubscribe && unsubscribe();
    }, [chatId, setActiveChat, subscribeToMessages, markChatAsRead]);

    // Handle initial message from HomePage exactly once
    useEffect(() => {
        if (location.state?.initialMessage && !initialPromptHandled.current) {
            initialPromptHandled.current = true;
            const initialPrompt = location.state.initialMessage;

            // Clear the location state immediately to prevent re-sends on re-render/refresh
            window.history.replaceState({}, document.title);

            // Wait slightly for connection to establish before sending
            setTimeout(() => {
                sendMessage(chatId, initialPrompt);
            }, 500);
        }
    }, [chatId, location.state, sendMessage]);

    // 2. Fetch Members (for Header & Avatars)
    useEffect(() => {
        const fetchMembers = async () => {
            if (!activeChat?.participants) return;
            const uniqueIds = [...new Set(activeChat.participants)];
            const memberData = [];

            for (const uid of uniqueIds) {
                const snap = await getDoc(doc(db, 'users', uid));
                if (snap.exists()) {
                    memberData.push({ uid, ...snap.data() });
                }
            }
            setMembers(memberData);
        };
        fetchMembers();
    }, [activeChat]);

    // 3. Auto-scroll
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, isAiThinking]);

    const handleSend = async (e) => {
        if (e) e.preventDefault();
        if (!inputText.trim()) return;

        const text = inputText;
        const options = isGhostMode ? { isDirectCommand: true, forceTool: 'antigravity_coder' } : {};

        setInputText(''); // Optimistic clear
        await sendMessage(chatId, text, options);
    };

    const handleMention = (name) => {
        setInputText((prev) => `${prev}@${name} `);
        inputRef.current?.focus();
    };

    const handleLocalBrainToggle = async (e) => {
        if (activeChat) {
            await updateChatSettings(activeChat.id, { localBrainEnabled: e.target.checked });
        }
    };

    const handleRunTerminal = async () => {
        if (!cmdInput.trim()) return;
        if (!activeChat?.targetDeviceId) {
            alert("No target computer selected for this space.");
            return;
        }
        setTerminalOpen(false);
        setCmdInput('');

        await DesktopService.sendDesktopCommand(user.uid, activeChat.targetDeviceId, {
            command: 'run_terminal',
            cmd: cmdInput,
            chatId: chatId
        });
    };

    const handleFileInstruction = async () => {
        if (!fileInstruction.trim()) return;
        setFilesOpen(false);
        setFileInstruction('');

        await sendMessage(chatId, `File Organization Request: ${fileInstruction}`, {
            isDirectCommand: true,
            forceTool: 'antigravity_coder'
        });
    };

    const handleRemoteShutdown = async () => {
        try {
            const targetUid = activeChat?.participants?.[0] || user?.uid;
            if (!targetUid) return;
            if (!activeChat?.targetDeviceId) {
                alert("No target computer selected for this space.");
                return;
            }

            await DesktopService.sendDesktopCommand(targetUid, activeChat.targetDeviceId, { command: 'quit_agent' }, chatId);
            setShutdownOpen(false);
        } catch (err) {
            console.error("Shutdown error:", err);
        }
    };

    const headerMembers = members.filter(m => m.uid !== user?.uid);

    return (
        <div className="flex flex-col h-screen bg-[var(--bg-main)] text-[var(--text-main)] font-sans transition-colors duration-300">
            {/* HEADER */}
            <header className="sticky top-0 z-20 bg-[var(--bg-main)] border-b border-[var(--border-subtle)] px-2 md:px-4 py-2 md:py-3 flex items-center h-14 shrink-0 transition-colors">
                <button onClick={() => navigate(-1)} className="p-2 mr-1 rounded-full hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-main)] transition-colors focus:outline-none">
                    <BackIcon className="w-4 h-4" />
                </button>

                {/* Header Avatars */}
                <div className="flex -space-x-1.5 mr-3 md:mr-4 relative shrink-0">
                    <div className="w-7 h-7 rounded-full bg-[var(--bg-tertiary)] border border-[var(--border-subtle)] flex items-center justify-center overflow-hidden z-20">
                        {activeChat?.expertAvatar ? <img src={activeChat.expertAvatar} alt="Expert" className="w-full h-full object-cover" /> : <BotIcon className="w-3.5 h-3.5 text-[var(--text-muted)]" />}
                    </div>
                    {headerMembers.map((m, i) => (
                        <div key={m.uid} title={m.displayName} className={`w-7 h-7 rounded-full bg-[var(--bg-tertiary)] border border-[var(--border-subtle)] overflow-hidden z-${10 - i}`}>
                            {m.photoURL ? <img src={m.photoURL} alt={m.displayName} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[9px] font-bold text-white bg-neutral-600">{m.displayName?.charAt(0)}</div>}
                        </div>
                    ))}
                </div>

                <div className="flex flex-col overflow-hidden min-w-0 pr-4">
                    {isEditingTitle ? (
                        <input
                            type="text"
                            value={titleInput}
                            onChange={(e) => setTitleInput(e.target.value)}
                            onBlur={handleSaveTitle}
                            onKeyDown={(e) => e.key === 'Enter' && handleSaveTitle()}
                            autoFocus
                            className="bg-[var(--bg-tertiary)] border-b-2 border-[var(--accent-primary)] text-[var(--text-main)] px-1 py-0.5 outline-none text-sm font-semibold w-48 rounded-t"
                        />
                    ) : (
                        <div className="flex items-center gap-2 min-w-0 group cursor-pointer" onClick={() => setIsEditingTitle(true)}>
                            <h1 className="text-sm font-semibold text-[var(--text-main)] truncate">
                                {activeChat?.title || "Untitled Chat"}
                            </h1>
                            <button className="text-[var(--text-muted)] opacity-0 group-hover:opacity-100 transition-opacity">
                                <EditIcon className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    )}
                    <span className="text-xs text-[var(--text-muted)] truncate tracking-wide">
                        {activeChat?.createdAt?.seconds
                            ? `Created ${new Date(activeChat.createdAt.seconds * 1000).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}`
                            : 'New Chat'}
                        {headerMembers.length > 0 && ` • with ${headerMembers.map(m => m.displayName.split(' ')[0]).join(', ')}`}
                    </span>
                </div>

                <div className="flex-grow"></div>
            </header>

            {/* MESSAGE LIST - Full Width Scroll Area */}
            <div className="flex-1 overflow-y-auto w-full scroll-smooth custom-scrollbar" style={{ background: 'transparent' }}>
                <div className="max-w-3xl mx-auto p-4 flex flex-col pt-6 space-y-8">
                    {messages?.length === 0 && !loading && (
                        <div className="flex flex-col items-center justify-center h-full opacity-40">
                            <div className="w-16 h-16 rounded-3xl bg-[var(--bg-tertiary)] border border-[var(--border-strong)] flex items-center justify-center mb-4">
                                <BotIcon className="w-8 h-8 text-[var(--text-muted)]" />
                            </div>
                            <p className="text-sm font-medium tracking-tight">No messages yet. Say hello to {activeChat?.expertName || 'Omni'}.</p>
                        </div>
                    )}

                    {loading && (!messages || messages.length === 0) && (
                        <div className="flex justify-center mt-10">
                            <svg className="animate-spin h-6 w-6 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                        </div>
                    )}

                    {messages?.map((msg) => {
                        const isMe = msg.senderId === user?.uid;
                        const senderProfile = members.find(m => m.uid === msg.senderId);
                        const isExpert = !isMe && !senderProfile;
                        const avatarSrc = senderProfile ? senderProfile.photoURL : (msg.expertAvatar || activeChat?.expertAvatar);
                        const name = senderProfile ? senderProfile.displayName : (isMe ? null : (msg.expertName || activeChat?.expertName));
                        const showAvatar = !isMe;
                        const showName = !isMe && senderProfile; // Only show name for other human members, not AI

                        return (
                            <div key={msg.id} className={`flex w-full ${isMe ? 'justify-end' : 'justify-start'}`}>
                                <div className="flex max-w-[85%] items-end gap-3 group">
                                    {!isMe && (
                                        <div className="w-8 h-8 rounded-full overflow-hidden shrink-0 mb-[18px] bg-[var(--bg-tertiary)] shadow-md border border-[var(--border-subtle)] flex items-center justify-center" title={name || 'Expert'}>
                                            {avatarSrc ? <img src={avatarSrc} alt={name} className="w-full h-full object-cover" /> : <BotIcon className="w-4 h-4 text-indigo-400" />}
                                        </div>
                                    )}

                                    <div className={`flex flex-col ${isMe ? 'items-end' : 'items-start'} min-w-0`}>
                                        {showName && (
                                            <span className="text-[11px] font-medium text-[var(--text-muted)] mb-1 ml-1">{name}</span>
                                        )}
                                        <div className={`
                                        px-4 py-2.5 rounded-2xl shadow-sm text-sm leading-relaxed break-words
                                        ${isMe
                                                ? 'bg-indigo-600 text-white rounded-br-sm'
                                                : 'bg-[var(--bg-secondary)] text-[var(--text-main)] border border-[var(--border-subtle)] rounded-bl-sm'}
                                    `}>
                                            <RichMessageRenderer message={msg} isMe={isMe} chatId={chatId} />
                                        </div>
                                        <span className={`text-[10px] text-[var(--text-muted)] mt-1 opacity-0 group-hover:opacity-100 transition-opacity ${isMe ? 'mr-1' : 'ml-1'}`}>
                                            {msg.createdAt?.seconds ? new Date(msg.createdAt.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '...'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {/* Thinking Indicator */}
                    {(isAiThinking || (messages.length > 0 && messages[messages.length - 1].senderId === 'AI' && messages[messages.length - 1].isThinking)) && (
                        <div className="flex w-full justify-start animate-in fade-in duration-300">
                            <div className="flex max-w-[85%] items-end gap-3 group">
                                <div className="w-8 h-8 rounded-full overflow-hidden shrink-0 bg-[var(--bg-tertiary)] shadow-md border border-[var(--border-subtle)] flex items-center justify-center">
                                    {activeChat?.expertAvatar ? <img src={activeChat.expertAvatar} alt="Thinking" className="w-full h-full object-cover" /> : <BotIcon className="w-4 h-4 text-indigo-400" />}
                                </div>
                                <div className="flex flex-col items-start min-w-0">
                                    <div className="px-4 py-2.5 rounded-2xl shadow-sm text-sm bg-[var(--bg-secondary)] text-[var(--text-main)] border border-[var(--border-subtle)] rounded-bl-sm flex items-center gap-1">
                                        <span className="dot-pulse"></span>
                                        <span className="dot-pulse"></span>
                                        <span className="dot-pulse"></span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div ref={messagesEndRef} className="h-4" />
                </div>
            </div>

            {/* INPUT AREA */}
            <div className="sticky bottom-0 z-10 w-full bg-[var(--bg-main)]">
                {((activeChat?.desktopEnabled || activeChat?.localBrainEnabled) && !isOnline && !window.__TAURI_INTERNALS__) && (
                    <div className="bg-orange-500/10 border-t border-orange-500/20 text-orange-400 text-[10px] font-bold uppercase tracking-wider text-center py-2">
                        ⚠️ Desktop Offline. Local Brain & Desktop tools are unavailable.
                    </div>
                )}


                <div className={`px-2 md:px-4 pb-4 md:pb-8 pt-2 transition-all duration-300 max-w-3xl mx-auto w-full`}>

                    {/* Desktop Action Bar (Floating above) - Always show if device is online or if it's the desktop app */}
                    {(activeChat?.desktopEnabled || isOnline || !!window.__TAURI_INTERNALS__) && (
                        <div className="flex items-center flex-wrap gap-2 mb-3 px-2 animate-in fade-in slide-in-from-bottom-2 duration-500">
                            <button type="button" title="Prompt IDE (Ghost Mode)" onClick={() => { setIsGhostMode(true); inputRef.current?.focus(); setInputText("Open Project: "); }} className={`p-2 rounded-xl transition-all ${isGhostMode ? 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/30' : 'hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-main)] border border-transparent hover:border-[var(--border-strong)]'}`}>
                                <CodeIcon className="w-4 h-4" />
                            </button>
                            <button
                                type="button"
                                title="Run Terminal"
                                onClick={() => navigate(`/desktop/terminal/${activeChat?.targetDeviceId || resolvedDeviceId}`)}
                                className="p-2 rounded-xl hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-main)] border border-transparent hover:border-[var(--border-strong)] transition-all"
                            >
                                <TerminalIcon className="w-4 h-4" />
                            </button>
                            <button
                                type="button"
                                title="Organize Files"
                                onClick={() => setFilesOpen(true)}
                                className="p-2 rounded-xl hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-main)] border border-transparent hover:border-[var(--border-strong)] transition-all"
                            >
                                <FolderIcon className="w-4 h-4" />
                            </button>
                            <button
                                type="button"
                                title="Live Desktop View"
                                onClick={() => navigate(`/desktop/live/${activeChat?.targetDeviceId || resolvedDeviceId}`)}
                                className="p-2 rounded-xl hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-main)] border border-transparent hover:border-[var(--border-strong)] transition-all"
                            >
                                <VideoIcon className="w-4 h-4" />
                            </button>
                            <button type="button" title="Shut Down Agent" onClick={() => setShutdownOpen(true)} className="p-2 rounded-xl hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-main)] border border-transparent hover:border-[var(--border-strong)] transition-all">
                                <PowerIcon className="w-4 h-4" />
                            </button>

                            <div className="h-4 w-[1px] bg-[var(--border-strong)] mx-1" />

                            <button type="button" title="Toggle Ghost Mode" onClick={() => setIsGhostMode(!isGhostMode)} className={`p-2 rounded-xl transition-all flex items-center gap-2 ${isGhostMode ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/20' : 'hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-main)] border border-transparent hover:border-[var(--border-strong)]'}`}>
                                <BotIcon className="w-4 h-4" />
                                <span className="text-[10px] font-bold uppercase tracking-widest px-1">Ghost</span>
                            </button>
                        </div>
                    )}

                    <PromptInput
                        value={inputText}
                        onChange={setInputText}
                        onSubmit={handleSend}
                        placeholder={activeChat?.localBrainEnabled ? `Ask Ollama (${activeChat.localModel})...` : (isGhostMode ? "👻 Enter Direct Desktop Command..." : "Message Omni...")}
                        selectedModelConfigId={activeChat?.localBrainEnabled ? { provider: 'ollama', model: activeChat.localModel } : activeChat?.modelConfigId}
                        onModelSelect={handleModelSelect}
                        selectedExpertId={activeChat?.expertId}
                        onExpertSelect={handleExpertSelect}
                        isGhostMode={isGhostMode}
                        autoFocus={true}
                    />
                </div>
            </div>

            {/* --- MODALS --- */}

            {/* Terminal Dialog */}
            {terminalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in">
                    <div className="bg-[var(--bg-main)] border border-[var(--border-subtle)] rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
                        <div className="p-4 border-b border-slate-800/50 flex gap-2 items-center">
                            <TerminalIcon className="w-5 h-5 text-indigo-400" />
                            <h2 className="text-lg font-semibold text-slate-100">Run Terminal Command</h2>
                        </div>
                        <div className="p-5">
                            <input
                                autoFocus
                                type="text"
                                className="w-full bg-[var(--bg-secondary)] border border-[var(--border-subtle)] text-green-400 font-mono text-sm p-3 rounded-xl outline-none focus:border-[var(--accent-primary)]"
                                value={cmdInput}
                                onChange={(e) => setCmdInput(e.target.value)}
                                placeholder="ls -la"
                                onKeyDown={(e) => e.key === 'Enter' && handleRunTerminal()}
                            />
                        </div>
                        <div className="p-4 bg-[var(--bg-secondary)]/50 flex justify-end gap-3 border-t border-[var(--border-subtle)]">
                            <button onClick={() => setTerminalOpen(false)} className="px-4 py-2 text-sm font-medium text-[var(--text-muted)] hover:text-[var(--text-main)] transition-colors">Cancel</button>
                            <button onClick={handleRunTerminal} className="px-5 py-2 text-sm font-medium bg-[var(--accent-primary)] hover:bg-[var(--accent-hover)] text-white rounded-lg shadow-md transition-colors">Execute</button>
                        </div>
                    </div>
                </div>
            )}

            {/* File Org Dialog */}
            {filesOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in">
                    <div className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
                        <div className="p-4 border-b border-slate-800/50 flex gap-2 items-center">
                            <FolderIcon className="w-5 h-5 text-blue-400" />
                            <h2 className="text-lg font-semibold text-slate-100">Organize Files</h2>
                        </div>
                        <div className="p-5">
                            <p className="text-sm text-slate-400 mb-4">Instruct the AI Agent to perform file operations on your desktop.</p>
                            <textarea
                                autoFocus
                                rows={3}
                                className="w-full bg-[#0F172A] border border-slate-700 text-slate-200 text-sm p-3 rounded-xl outline-none focus:border-blue-500 resize-none"
                                value={fileInstruction}
                                onChange={(e) => setFileInstruction(e.target.value)}
                                placeholder="Move all .png files from Desktop to Pictures folder..."
                            />
                        </div>
                        <div className="p-4 bg-slate-800/30 flex justify-end gap-3 border-t border-slate-800/50">
                            <button onClick={() => setFilesOpen(false)} className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors">Cancel</button>
                            <button onClick={handleFileInstruction} className="px-5 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-500 text-white rounded-lg shadow-md transition-colors">Instruct Agent</button>
                        </div>
                    </div>
                </div>
            )}

            <LiveStreamModal open={isLiveOpen} onClose={() => setIsLiveOpen(false)} targetDeviceId={activeChat?.targetDeviceId} targetUid={activeChat?.participants?.[0] || user?.uid} />

            {/* Shutdown Confirmation */}
            {shutdownOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-md animate-in fade-in">
                    <div className="bg-[var(--bg-main)] border border-[var(--border-subtle)] rounded-3xl shadow-2xl w-full max-w-sm p-6 text-center" onClick={e => e.stopPropagation()}>
                        <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4 border border-red-500/20">
                            <PowerIcon className="w-8 h-8 text-red-500" />
                        </div>
                        <h2 className="text-xl font-bold text-slate-100 mb-2">Shut Down Agent?</h2>
                        <p className="text-sm text-slate-400 mb-6">This will completely terminate the Antigravity background sidecar on this machine. Remote access will be lost.</p>
                        <div className="flex gap-3">
                            <button onClick={() => setShutdownOpen(false)} className="flex-1 px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-200 font-medium rounded-xl transition-colors">Cancel</button>
                            <button onClick={handleRemoteShutdown} className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-500 text-white font-medium rounded-xl shadow-lg shadow-red-500/25 transition-colors">Shut Down</button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default ChatRoomPage;
